import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Eye, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import RegistrationDetail from "@/pages/admin/RegistrationDetail";
import RegistrationStats from "@/pages/admin/RegistrationStats";

const TABS = [
  ["pending", "Nouvelles demandes"], ["approved", "Approuvées"], ["rejected", "Refusées"],
  ["suspended", "Comptes suspendus"], ["all", "Tous les utilisateurs"], ["stats", "Statistiques"],
];
const ROLE = { admin: "Admin", directeur: "Directeur", maitre: "Enseignant", inspecteur: "Inspecteur" };

function RequestsTable({ status, onOpen, version }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/registrations", { params: { status, q: q || undefined } }).then(({ data }) => setRows(data)).catch((e) => toast.error(formatError(e))), 250);
    return () => clearTimeout(t);
  }, [status, q, version]);
  return (
    <div className="ek-card p-0">
      <div className="p-3 border-b border-[#E2E8F0]">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C736A]" />
          <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, école, téléphone, IA, IEF…" data-testid="registrations-search" />
        </div>
      </div>
      <div className="ek-table-wrap">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>N° demande</TableHead><TableHead>Prénom et nom</TableHead><TableHead>Profil</TableHead>
            <TableHead>Établissement</TableHead><TableHead className="hidden lg:table-cell">IA</TableHead><TableHead className="hidden lg:table-cell">IEF</TableHead>
            <TableHead className="hidden md:table-cell">Classes</TableHead><TableHead>Téléphone</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => onOpen(r.id)} data-testid={`registration-row-${r.username}`}>
                <TableCell className="whitespace-nowrap text-sm">{new Date(r.created_at).toLocaleDateString("fr-FR")}</TableCell>
                <TableCell className="font-mono text-xs">{r.numero}</TableCell>
                <TableCell className="font-semibold">{r.name}</TableCell>
                <TableCell>{ROLE[r.profil]}</TableCell>
                <TableCell className="max-w-[180px] truncate">{r.school?.nom}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm">{r.school?.ia || "—"}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm">{r.school?.ief || "—"}</TableCell>
                <TableCell className="hidden md:table-cell">{r.profil === "maitre" ? (r.niveaux || []).length : r.school?.nb_classes ?? "—"}</TableCell>
                <TableCell className="whitespace-nowrap">{r.telephone}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpen(r.id); }}><Eye className="w-4 h-4 mr-1" /> Voir</Button></TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={11} className="text-center text-sm text-[#5C736A] py-10">Aucune demande.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AccountsTable({ status }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/accounts", { params: { status: status || undefined } }).then(({ data }) => setRows(data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const toggle = async (u) => {
    const action = u.status === "suspended" ? "reactivate" : "suspend";
    if (!window.confirm(action === "suspend" ? `Suspendre le compte de ${u.name} ?` : `Réactiver le compte de ${u.name} ?`)) return;
    try { await api.post(`/admin/users/${u.id}/${action}`); toast.success(action === "suspend" ? "Compte suspendu" : "Compte réactivé"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="ek-card p-0 ek-table-wrap">
      <Table>
        <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Identifiant</TableHead><TableHead>Rôle</TableHead><TableHead className="hidden md:table-cell">Téléphone</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((u) => (
            <TableRow key={u.id} data-testid={`account-row-${u.username}`}>
              <TableCell className="font-semibold">{u.name}</TableCell><TableCell>{u.username}</TableCell><TableCell>{ROLE[u.role] || u.role}</TableCell>
              <TableCell className="hidden md:table-cell">{u.telephone || "—"}</TableCell><TableCell><StatusBadge user={u} /></TableCell>
              <TableCell className="text-right">
                {u.role !== "admin" && ["approved", "suspended"].includes(u.status) && (
                  <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`account-toggle-${u.username}`}>
                    {u.status === "suspended" ? <><PlayCircle className="w-4 h-4 mr-1" /> Réactiver</> : <><PauseCircle className="w-4 h-4 mr-1" /> Suspendre</>}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#5C736A] py-10">Aucun compte.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function RefusalSetting() {
  const [v, setV] = useState(false);
  useEffect(() => { api.get("/admin/registration-settings").then(({ data }) => setV(data.refusal_reason_required)).catch(() => {}); }, []);
  const change = async (val) => {
    setV(val);
    try { await api.put("/admin/registration-settings", { refusal_reason_required: val }); toast.success("Règle enregistrée"); } catch (e) { toast.error(formatError(e)); }
  };
  return <label className="flex items-center gap-2 text-sm"><Switch checked={v} onCheckedChange={change} data-testid="refusal-required-switch" /> Motif de refus obligatoire</label>;
}

export default function Registrations() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "pending";
  const openId = params.get("id");
  const [version, setVersion] = useState(0);
  const setP = (patch) => { const p = Object.fromEntries(params); Object.entries(patch).forEach(([k, v]) => (v ? (p[k] = v) : delete p[k])); setParams(p, { replace: true }); };

  return (
    <div className="space-y-5" data-testid="registrations-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Demandes d&apos;inscription</h1>
          <p className="text-sm text-[#5C736A]">Aucun compte n&apos;est activé sans votre validation. <Link to="/app/users" className="text-[#1F7A63] font-semibold">Gérer les utilisateurs</Link></p>
        </div>
        <RefusalSetting />
      </div>
      <Tabs value={tab} onValueChange={(v) => setP({ tab: v })}>
        <TabsList className="w-full overflow-x-auto flex justify-start">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`reg-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {["pending", "approved", "rejected"].includes(tab) && <RequestsTable status={tab} onOpen={(id) => setP({ id })} version={version} />}
      {tab === "suspended" && <AccountsTable status="suspended" />}
      {tab === "all" && <AccountsTable />}
      {tab === "stats" && <RegistrationStats onOpen={(id) => setP({ id })} />}
      <RegistrationDetail id={openId} onClose={() => setP({ id: null })} onChanged={() => setVersion((v) => v + 1)} />
    </div>
  );
}
